import { useState, useEffect, useCallback } from 'react';
import {
  isAvailable,
  getModelStatus,
  getUnavailableReason,
  validate,
  suggest,
  splitActivity,
  analyzeGoalCoverage,
  generateGapGoals,
  analyzeActivityCoverage,
  generateGapActivities,
} from '../services/llm';
import type {
  ValidateResult,
  SuggestResult,
  SplitResult,
  GoalCoverageResult,
  GeneratedGoal,
  ActivityCoverageResult,
  GeneratedActivity,
} from '../services/llm';
import { useAiSettingsStore } from '../stores/aiSettingsStore';

interface UseLlmState {
  available: boolean | null; // null = still checking
  modelDownloading: boolean;
  unavailableReason: string;
  loading: boolean;
  error: string | null;
}

export function useOnDeviceLlm() {
  const aiEnabled = useAiSettingsStore((s) => s.aiEnabled);
  const settingsLoaded = useAiSettingsStore((s) => s.isInitialized);
  const customPrompts = useAiSettingsStore((s) => s.customPrompts);

  const [state, setState] = useState<UseLlmState>({
    available: null,
    modelDownloading: false,
    unavailableReason: '',
    loading: false,
    error: null,
  });

  useEffect(() => {
    // The store's aiEnabled=false is a default until the account's settings
    // arrive, not the user's choice — saying "disabled" then is wrong. Stay in
    // the checking state (available: null) until they load.
    if (!settingsLoaded) return;

    // If AI is disabled by user, short-circuit
    if (!aiEnabled) {
      setState((prev) => ({
        ...prev,
        available: false,
        modelDownloading: false,
        unavailableReason: 'AI features are disabled. Enable them in Settings > AI Features.',
      }));
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const avail = await isAvailable();
        if (cancelled) return;
        let reason = '';
        let downloading = false;
        if (!avail) {
          reason = await getUnavailableReason();
        } else {
          const status = await getModelStatus();
          downloading = status === 'downloading';
        }
        setState((prev) => ({
          ...prev,
          available: avail,
          modelDownloading: downloading,
          unavailableReason: reason,
        }));
      } catch {
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            available: false,
            modelDownloading: false,
            unavailableReason: 'Failed to check AI availability.',
          }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [aiEnabled, settingsLoaded]);

  const runValidate = useCallback(
    async (
      entityType: string,
      text: string
    ): Promise<ValidateResult | null> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const result = await validate(entityType, text, customPrompts);
        setState((prev) => ({ ...prev, loading: false }));
        return result;
      } catch (e: any) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: e.message ?? 'AI validation failed',
        }));
        return null;
      }
    },
    [customPrompts]
  );

  const runSuggest = useCallback(
    async (
      entityType: string,
      text: string
    ): Promise<SuggestResult | null> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const result = await suggest(entityType, text, customPrompts);
        setState((prev) => ({ ...prev, loading: false }));
        return result;
      } catch (e: any) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: e.message ?? 'AI suggestion failed',
        }));
        return null;
      }
    },
    [customPrompts]
  );

  const runSplitActivity = useCallback(
    async (
      activityText: string,
      estimatedMinutes: number
    ): Promise<SplitResult | null> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const result = await splitActivity(activityText, estimatedMinutes, customPrompts);
        setState((prev) => ({ ...prev, loading: false }));
        return result;
      } catch (e: any) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: e.message ?? 'AI activity split failed',
        }));
        return null;
      }
    },
    [customPrompts]
  );

  const runAnalyzeGoalCoverage = useCallback(
    async (
      visionText: string,
      goalTitles: string[]
    ): Promise<GoalCoverageResult | null> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const result = await analyzeGoalCoverage(visionText, goalTitles, customPrompts);
        setState((prev) => ({ ...prev, loading: false }));
        return result;
      } catch (e: any) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: e.message ?? 'AI goal coverage analysis failed',
        }));
        return null;
      }
    },
    [customPrompts]
  );

  const runGenerateGapGoals = useCallback(
    async (
      visionTitle: string,
      gapDimensions: string[]
    ): Promise<GeneratedGoal[] | null> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const result = await generateGapGoals(visionTitle, gapDimensions, customPrompts);
        setState((prev) => ({ ...prev, loading: false }));
        return result;
      } catch (e: any) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: e.message ?? 'AI goal generation failed',
        }));
        return null;
      }
    },
    [customPrompts]
  );

  const runAnalyzeActivityCoverage = useCallback(
    async (
      goalTitle: string,
      goalDescription: string,
      activityTitles: string[]
    ): Promise<ActivityCoverageResult | null> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const result = await analyzeActivityCoverage(goalTitle, goalDescription, activityTitles, customPrompts);
        setState((prev) => ({ ...prev, loading: false }));
        return result;
      } catch (e: any) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: e.message ?? 'AI activity coverage analysis failed',
        }));
        return null;
      }
    },
    [customPrompts]
  );

  const runGenerateGapActivities = useCallback(
    async (
      goalTitle: string,
      goalDescription: string,
      gapDimensions: string[]
    ): Promise<GeneratedActivity[] | null> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const result = await generateGapActivities(goalTitle, goalDescription, gapDimensions, customPrompts);
        setState((prev) => ({ ...prev, loading: false }));
        return result;
      } catch (e: any) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: e.message ?? 'AI activity generation failed',
        }));
        return null;
      }
    },
    [customPrompts]
  );

  return {
    ...state,
    validate: runValidate,
    suggest: runSuggest,
    splitActivity: runSplitActivity,
    analyzeGoalCoverage: runAnalyzeGoalCoverage,
    generateGapGoals: runGenerateGapGoals,
    analyzeActivityCoverage: runAnalyzeActivityCoverage,
    generateGapActivities: runGenerateGapActivities,
  };
}
